import { normalizeParticipantIdInput } from "../validation/controlledInputs";

/**
 * Global-entry handoff helpers.
 *
 * These helpers keep the regional web-app tolerant of the public global-entry
 * redirect contract without making the whole app permanently query-driven.
 * The handoff currently carries:
 * - `clinicStudyId` to seed onboarding context
 * - `subjectId` to prefill the study-access participant field when a clinician
 *   shares a dashboard invite or QR
 * - `inviteToken` to preserve opaque invitation state
 * - `lng` to suggest the initial locale
 * - `source=global-entry` to mark the traffic origin
 *
 * The user may still change locale after arrival; we only auto-apply the
 * query-supplied locale once per distinct URL locale value.
 *
 * @module navigation/globalEntryHandoff
 */

const GLOBAL_ENTRY_SOURCE = "global-entry";

function clean(value) {
  if (value === undefined || value === null) return null;

  const next = String(value).trim();
  return next.length ? next : null;
}

export function normalizeGlobalEntryClinicStudyId(value) {
  const cleaned = clean(value);
  return cleaned ? cleaned.toUpperCase() : null;
}

export function normalizeGlobalEntryInviteToken(value) {
  return clean(value);
}

export function normalizeGlobalEntrySubjectId(value) {
  return normalizeParticipantIdInput(value);
}

export function normalizeGlobalEntryLocale(value) {
  const cleaned = clean(value);

  if (!cleaned) return null;

  const normalized = cleaned.toLowerCase();

  if (normalized.startsWith("ja")) return "ja";
  if (normalized === "en" || normalized === "en-gb") return "en-GB";
  if (normalized === "en-us") return "en-US";

  return null;
}

export function readGlobalEntryHandoff(search) {
  const params = new URLSearchParams(String(search || ""));
  const clinicStudyId = normalizeGlobalEntryClinicStudyId(
    params.get("clinicStudyId"),
  );
  const subjectId = normalizeGlobalEntrySubjectId(params.get("subjectId"));
  const inviteToken = normalizeGlobalEntryInviteToken(
    params.get("inviteToken"),
  );
  const lng = normalizeGlobalEntryLocale(params.get("lng"));
  const source = clean(params.get("source"));

  return {
    clinicStudyId,
    subjectId,
    inviteToken,
    lng,
    source,
    isGlobalEntrySource: source === GLOBAL_ENTRY_SOURCE,
  };
}

export function buildGlobalEntryHandoffSearch(search, options = {}) {
  const handoff = readGlobalEntryHandoff(search);
  const params = new URLSearchParams();

  if (options.includeClinicStudyId !== false && handoff.clinicStudyId) {
    params.set("clinicStudyId", handoff.clinicStudyId);
  }

  if (options.includeSubjectId !== false && handoff.subjectId) {
    params.set("subjectId", handoff.subjectId);
  }

  if (options.includeInviteToken !== false && handoff.inviteToken) {
    params.set("inviteToken", handoff.inviteToken);
  }

  if (options.includeLng !== false && handoff.lng) {
    params.set("lng", handoff.lng);
  }

  if (options.includeSource !== false && handoff.source) {
    params.set("source", handoff.source);
  }

  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

export function withGlobalEntryHandoff(path, search, options = {}) {
  return `${path}${buildGlobalEntryHandoffSearch(search, options)}`;
}
